import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { useState, useEffect } from 'react'

// -- Data ------------------------------------------------------------------

const PRINCIPLES = [
    {
        label: 'Diagnosis before prescription',
        body: 'Every GMC analysis begins with a structured diagnostic pass -- identifying the precise mechanisms that create risk before any rewrite is suggested. The score is a clinical finding, not a guess.',
    },
    {
        label: 'Zero retention by architecture',
        body: 'Message content is processed in memory and discarded. No database column for message content exists or will ever be added. This is a structural guarantee, not a policy promise.',
    },
    {
        label: 'Isolation between passes',
        body: 'Pass 2 receives only structured JSON from Pass 1 -- never the original message. This eliminates prompt injection as a risk surface and keeps the rewrite grounded in verified findings.',
    },
    {
        label: 'Output at expert standard',
        body: 'GMC is calibrated to write at the standard of the most senior professional in whatever field the message comes from -- automatically, based on context. Legal correspondence reads like a senior partner wrote it.',
    },
]

const COMPARISON = [
    {
        them: 'A general assistant, capable at everything and specialised in nothing.',
        us: 'A specialist built for one job: assessing communication risk before you send.',
    },
    {
        them: 'Answers vary between runs. The same message can get a different verdict tomorrow.',
        us: 'Reproducible scoring on a fixed scale. The same message returns the same result, every time.',
    },
    {
        them: 'Your message is transmitted, retained and governed by a third party\u2019s terms.',
        us: 'Zero retention by architecture. There is no database column for message content anywhere in GMC.',
    },
    {
        them: 'General writing advice: this could come across as harsh.',
        us: 'Mechanism-specific analysis: which phrase, which risk it activates, and what it tends to trigger.',
    },
    {
        them: 'A chat window. Compose a prompt, read an essay, work out what to do.',
        us: 'A purpose-built workflow. Paste, analyse, receive a clear assessment and a rewrite if you want one.',
    },
]

// -- Component -------------------------------------------------------------

export default function About() {
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const t = setTimeout(() => setVisible(true), 60)
        return () => clearTimeout(t)
    }, [])

    const fade = visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'

    return (
        <div className="min-h-screen flex flex-col bg-brand-navy">
            <Navbar />

            {/* -- Hero -------------------------------------------------- */}
            <section className="relative overflow-hidden">
                {/* Ambient glow */}
                <div className="absolute inset-0 pointer-events-none"
                    style={{ background: 'radial-gradient(ellipse 60% 50% at 70% 40%, rgba(212,168,67,0.06) 0%, transparent 70%)' }} />

                <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-24 sm:pt-28 sm:pb-32 relative z-10">
                    <div className={`transition-all duration-700 ease-out ${fade}`}>
                        <p className="text-brand-amber text-xs font-body uppercase tracking-[0.25em] mb-6">
                            About Guardian Message Check
                        </p>
                        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-brand-text leading-[1.05] tracking-tight mb-8 max-w-4xl">
                            Professional communication<br className="hidden sm:block" />
                            carries more risk than<br className="hidden sm:block" />
                            <span className="text-brand-amber">most people realise.</span>
                        </h1>
                        <p className="text-brand-text font-body text-lg sm:text-xl leading-relaxed max-w-2xl mb-5">
                            Guardian Message Check helps professionals identify communication
                            risk before they press send.
                        </p>
                        <p className="text-brand-muted font-body text-base leading-relaxed max-w-2xl">
                            Built for the moment when the stakes are real, the recipient matters,
                            and the wording is not quite right but you cannot articulate why.
                        </p>
                    </div>
                </div>

                {/* Bottom rule */}
                <div className="border-t border-brand-border" />
            </section>

            {/* -- The problem ------------------------------------------- */}
            <section className="border-b border-brand-border">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                        <div>
                            <p className="text-brand-amber text-xs font-body uppercase tracking-[0.2em] mb-5">
                                The problem
                            </p>
                            <h2 className="font-display font-bold text-3xl sm:text-4xl text-brand-text leading-tight mb-0">
                                The message you send becomes a permanent record.
                            </h2>
                        </div>
                        <div className="space-y-5 text-brand-muted font-body text-sm sm:text-base leading-relaxed">
                            <p>
                                A single phrase -- "we need to reconsider your role" -- can transform
                                a performance conversation into an undocumented termination threat.
                                A poorly framed escalation email can destroy a client relationship
                                that took years to build. An informal message that "feels fine" today
                                becomes evidence tomorrow.
                            </p>
                            <p>
                                Most professionals have no tool for this moment. They rely on instinct,
                                re-reading their own words until they can no longer see them clearly.
                                GMC exists to be the second pair of eyes that identifies the
                                specific mechanism creating risk -- and rewrites it with the precision
                                that removes it.
                            </p>
                            <p>
                                Think of it as a diagnostic instrument. It identifies the problem,
                                names the mechanism, scores the severity -- and then prescribes.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* -- Reproducibility --------------------------------------- */}
            <section className="border-b border-brand-border relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none"
                    style={{ background: 'radial-gradient(ellipse 55% 60% at 30% 50%, rgba(212,168,67,0.07) 0%, transparent 70%)' }} />
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28 relative z-10">
                    <p className="text-brand-amber text-xs font-body uppercase tracking-[0.2em] mb-10">
                        Consistency
                    </p>
                    <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-brand-text leading-[1.1] tracking-tight mb-8 max-w-3xl">
                        Every message is analysed the same way,{' '}
                        <span className="text-brand-amber">every time.</span>
                    </h2>
                    <p className="text-brand-text font-body text-lg sm:text-xl leading-relaxed max-w-2xl mb-6">
                        A risk assessment should be reproducible, not opinion.
                    </p>
                    <p className="text-brand-muted font-body text-base leading-relaxed max-w-2xl">
                        Ask a general AI assistant to review the same message on Monday and again
                        on Friday and you will get two fluent, differently worded answers, with no
                        way to know which to trust. GMC returns the same score for the same message
                        every time, on a fixed scale, proved by tests rather than asserted. That is
                        the difference between an opinion and an assessment -- and it is what makes
                        a result something you can put in a file, quote in a process, and compare
                        across a team.
                    </p>
                </div>
            </section>

            {/* -- Why not just use ChatGPT? ----------------------------- */}
            <section className="border-b border-brand-border">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                    <p className="text-brand-amber text-xs font-body uppercase tracking-[0.2em] mb-8">
                        The obvious question
                    </p>
                    <h2 className="font-display font-bold text-3xl sm:text-4xl text-brand-text leading-tight tracking-tight mb-6 max-w-3xl">
                        Why not just use ChatGPT?
                    </h2>
                    <p className="text-brand-muted font-body text-base leading-relaxed max-w-2xl mb-14">
                        A fair question, and one worth answering directly. General assistants are
                        remarkable tools. They are simply built for a different job than this one.
                    </p>

                    <div className="border border-brand-border rounded-xl overflow-hidden">
                        {/* Column headings */}
                        <div className="hidden sm:grid grid-cols-2 bg-white/[0.02] border-b border-brand-border">
                            <div className="px-6 py-4">
                                <span className="text-brand-muted font-body text-xs uppercase tracking-widest">
                                    General AI assistant
                                </span>
                            </div>
                            <div className="px-6 py-4 border-l border-brand-border">
                                <span className="text-brand-amber font-body text-xs uppercase tracking-widest font-semibold">
                                    Guardian Message Check
                                </span>
                            </div>
                        </div>

                        {COMPARISON.map((row, i) => (
                            <div key={row.them}
                                className={`grid grid-cols-1 sm:grid-cols-2 ${i < COMPARISON.length - 1 ? 'border-b border-brand-border' : ''}`}>
                                <div className="px-6 py-6 flex items-start gap-3">
                                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-muted/50 shrink-0" />
                                    <span className="text-brand-muted font-body text-sm leading-relaxed">
                                        {row.them}
                                    </span>
                                </div>
                                <div className="px-6 py-6 flex items-start gap-3 border-t sm:border-t-0 sm:border-l border-brand-border bg-brand-amber/[0.015]">
                                    <span className="mt-1.5 shrink-0 text-brand-amber">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    </span>
                                    <span className="text-brand-text font-body text-sm leading-relaxed">
                                        {row.us}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className="text-brand-muted font-body text-sm leading-relaxed max-w-2xl mt-10">
                        Someone writing to a neighbour should use a general assistant -- it is free
                        and it is capable. Someone whose message may be read out at a tribunal
                        should not.
                    </p>
                </div>
            </section>

            {/* -- Principles -------------------------------------------- */}
            <section className="border-b border-brand-border">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                    <p className="text-brand-amber text-xs font-body uppercase tracking-[0.2em] mb-12">
                        Design principles
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-16 gap-y-12">
                        {PRINCIPLES.map((p) => (
                            <div key={p.label}>
                                <h3 className="font-display font-semibold text-lg text-brand-text mb-3 leading-snug">
                                    {p.label}
                                </h3>
                                <p className="text-brand-muted font-body text-sm leading-relaxed">
                                    {p.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* -- Who it is for ----------------------------------------- */}
            <section className="border-b border-brand-border">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                        <div>
                            <p className="text-brand-amber text-xs font-body uppercase tracking-[0.2em] mb-5">
                                Who it is for
                            </p>
                            <h2 className="font-display font-bold text-3xl sm:text-4xl text-brand-text leading-tight">
                                Professionals who cannot afford to get it wrong.
                            </h2>
                        </div>
                        <div>
                            <div className="space-y-4">
                                {[
                                    ['Lawyers and legal professionals', 'Correspondence that must be precise, defensible, and appropriate to the relationship context.'],
                                    ['HR directors and people managers', 'Performance conversations, disciplinary communications, and sensitive internal messages.'],
                                    ['Senior managers and executives', 'Upward challenge, escalation management, and high-stakes stakeholder communications.'],
                                    ['Finance and compliance professionals', 'Regulatory correspondence, client communications, and internal escalation.'],
                                    ['Individuals', 'Formal letters, solicitor communications, insurance claims, and any personal correspondence where tone and precision matter.'],
                                ].map(([role, desc]) => (
                                    <div key={role} className="flex gap-4 py-4 border-b border-brand-border/50 last:border-0">
                                        <span className="w-1.5 h-1.5 rounded-full bg-brand-amber flex-shrink-0 mt-2" />
                                        <div>
                                            <p className="text-brand-text font-body text-sm font-semibold mb-1">{role}</p>
                                            <p className="text-brand-muted font-body text-sm leading-relaxed">{desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* -- CTA --------------------------------------------------- */}
            <section>
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                    <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-10">
                        <div>
                            <h2 className="font-display font-bold text-3xl sm:text-4xl text-brand-text leading-tight mb-4">
                                Know the risk before<br />
                                <span className="text-brand-amber">you press send.</span>
                            </h2>
                            <p className="text-brand-muted font-body text-base max-w-lg leading-relaxed">
                                Three analyses free. No card required. See what GMC finds in a message
                                you have already sent -- and what it would have changed.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                            <Link to="/register" className="btn-primary text-center">
                                Start analysing
                            </Link>
                            <Link to="/plans" className="btn-ghost text-center">
                                View plans
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    )
}